
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { cn } from "@/lib/utils";
import { BigFiveQuestion } from '@/types/personality';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";

interface QuestionCardProps {
  question: BigFiveQuestion;
  currentAnswer?: number;
  onAnswer: (score: number) => void;
  onBack?: () => void;
  isFirst?: boolean;
}

export default function QuestionCard({ 
  question, 
  currentAnswer = 0, 
  onAnswer, 
  onBack,
  isFirst = false 
}: QuestionCardProps) {
  const [selectedValue, setSelectedValue] = useState<number>(currentAnswer);
  const [hasAnswered, setHasAnswered] = useState<boolean>(false);

  // Update selected value when currentAnswer changes
  useEffect(() => {
    setSelectedValue(currentAnswer);
    setHasAnswered(currentAnswer > 0);
  }, [currentAnswer]);

  // Handle option selection
  const handleSelect = (value: string) => {
    const score = parseInt(value, 10);
    setSelectedValue(score);
    setHasAnswered(true);
  };

  // Handle continue button click
  const handleContinue = () => {
    if (selectedValue > 0) {
      onAnswer(selectedValue);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto"
    >
      <h3 className="text-xl font-semibold mb-6">{question.text}</h3>

      <RadioGroup 
        value={selectedValue ? selectedValue.toString() : undefined}
        onValueChange={handleSelect}
        className="grid gap-4"
      >
        <div className="flex justify-between text-sm text-gray-500 mb-2 px-4">
          <span>Discordo totalmente</span>
          <span>Concordo totalmente</span>
        </div>
        
        <div className="flex justify-between gap-2">
          {[1, 2, 3, 4, 5].map((value) => (
            <label
              key={value}
              className="flex flex-col items-center gap-2 cursor-pointer"
            >
              <div className={cn(
                "w-14 h-14 rounded-full flex items-center justify-center border-2 transition-all",
                selectedValue === value 
                  ? "border-brand-blue bg-brand-blue bg-opacity-10" 
                  : "border-gray-200 hover:border-gray-300"
              )}>
                <RadioGroupItem 
                  value={value.toString()} 
                  id={`option-${value}`} 
                  className="sr-only"
                />
                <span className="text-lg font-medium">{value}</span>
              </div>
            </label>
          ))}
        </div>
      </RadioGroup>

      <div className="flex justify-between mt-8">
        {!isFirst && (
          <Button
            variant="outline"
            onClick={onBack}
          >
            Voltar
          </Button>
        )}
        
        <div className={cn("ml-auto", isFirst && "w-full")}>
          <Button
            onClick={handleContinue}
            disabled={!hasAnswered}
            className={cn("w-full", isFirst && "w-full")}
          >
            Continuar
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
